import React from 'react';
import { Link, NavLink } from 'react-router-dom';
import { useCountry } from '../context/CountryContext';

// Custom SVG Logo Component: Green/Blue Shield with Chip & Magnifying Glass
const LogoIcon: React.FC<{ className?: string }> = ({ className = "w-10 h-10" }) => (
  <svg viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
    <defs>
      <linearGradient id="headerLogoGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#4ADE80" /> {/* Green-400 */}
        <stop offset="50%" stopColor="#22D3EE" /> {/* Cyan-400 */}
        <stop offset="100%" stopColor="#3B82F6" /> {/* Blue-500 */}
      </linearGradient>
    </defs>
    
    {/* Shield Background (Dark) with Gradient Stroke */}
    <path 
      d="M50 5 L15 18 V45 C15 68 30 88 50 95 C70 88 85 68 85 45 V18 L50 5 Z" 
      fill="#0F172A" 
      stroke="url(#headerLogoGrad)" 
      strokeWidth="4" 
      strokeLinejoin="round"
    />
        
    {/* Magnifying Glass */}
    <circle cx="50" cy="45" r="18" stroke="url(#headerLogoGrad)" strokeWidth="4" fill="none"/>
    <line x1="63" y1="58" x2="75" y2="70" stroke="url(#headerLogoGrad)" strokeWidth="6" strokeLinecap="round"/>
    
    {/* Microchip Inside Lens */}
    <rect x="40" y="35" width="20" height="20" rx="2" stroke="url(#headerLogoGrad)" strokeWidth="2" fill="none"/>
    {/* Chip Contacts */}
    <path d="M40 40 H36 M40 45 H36 M40 50 H36 M60 40 H64 M60 45 H64 M60 50 H64 M45 35 V31 M50 35 V31 M55 35 V31 M45 55 V59 M50 55 V59 M55 55 V59" 
          stroke="url(#headerLogoGrad)" strokeWidth="2" strokeLinecap="round"/>
    {/* Chip Core */}
    <rect x="45" y="40" width="10" height="10" rx="1" fill="url(#headerLogoGrad)" fillOpacity="0.4"/>
  </svg>
);

const CountrySelector: React.FC = () => {
    const { country, setCountry, availableCountries } = useCountry();

    const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setCountry(event.target.value);
    };

    return (
        <div className="relative group">
            <select
                value={country}
                onChange={handleChange}
                className="appearance-none bg-gray-100 border-none text-gray-700 text-sm font-semibold rounded-md focus:ring-2 focus:ring-primary block w-full pl-9 pr-8 py-2 cursor-pointer hover:bg-gray-200 transition-colors outline-none"
                aria-label="Select a country"
            >
                {availableCountries.map(c => (
                    <option key={c.code} value={c.code}>
                        {c.name}
                    </option>
                ))}
            </select>
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-2.5 text-xl">
                 {availableCountries.find(c => c.code === country)?.flag}
            </div>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2 text-gray-500">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
            </div>
        </div>
    );
};

const Header: React.FC = () => {
  const { country } = useCountry();
  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    `whitespace-nowrap text-sm font-semibold transition-all duration-200 px-3 py-2 rounded-full ${
      isActive 
        ? 'bg-blue-100 text-primary' 
        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
    }`;
  
  const paymentAppPath = country === 'IN' ? '/upi-charges' : '/payment-app-charges';
  
  const navLinks = (
    <>
      <li><NavLink to="/" className={navLinkClass} end>Home</NavLink></li>
      <li><NavLink to="/charges" className={navLinkClass}>All Charges</NavLink></li>
      <li><NavLink to="/bank-charges" className={navLinkClass}>Banks</NavLink></li>
      <li><NavLink to={paymentAppPath} className={navLinkClass}>{country === 'IN' ? 'UPI' : 'Payment Apps'}</NavLink></li>
      <li><NavLink to="/credit-card-charges" className={navLinkClass}>Cards</NavLink></li>
      <li><NavLink to="/compare" className={navLinkClass}>Compare</NavLink></li>
      <li><NavLink to="/ai-decoder" className={navLinkClass}>Add a Charge</NavLink></li>
    </>
  );

  return (
    <header className="bg-white sticky top-0 z-50 border-b border-gray-100 shadow-sm">
      <div className="mx-auto max-w-screen-xl px-4 lg:px-6">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center">
            <Link to="/" className="flex items-center gap-2 group">
              <LogoIcon className="w-8 h-8 sm:w-10 sm:h-10 transition-transform group-hover:scale-105" />
              <span className="text-lg sm:text-xl font-bold text-gray-900 tracking-tight">ChargesDecoder</span>
            </Link>
          </div>
          
          <nav className="hidden lg:flex items-center gap-4">
            <ul className="flex items-center gap-2">{navLinks}</ul>
          </nav>

          <div className="flex items-center gap-4">
            <CountrySelector />
          </div>
        </div>
        
        {/* Mobile Navigation - Horizontal Scroll */}
        <div className="lg:hidden py-3 border-t border-gray-50 overflow-x-auto no-scrollbar bg-white">
            <ul className="flex items-center gap-3 px-4 min-w-max">
                {navLinks}
            </ul>
        </div>
      </div>
    </header>
  );
};

export default Header;